import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  getCategories, getProducts, getSecondHandItem,
  createSecondHandItem, updateSecondHandItem,
  uploadSecondHandImage, deleteSecondHandImage,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import { CONDITION_LABELS } from '../../components/common/SecondHandCard';
import { FiArrowLeft, FiUpload, FiX, FiStar, FiInfo, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './SellItem.css';

const EMPTY = {
  title: '', description: '', price: '', original_price: '',
  condition: 'good', usage_details: '', purchase_year: '',
  accessories: '', warranty_remaining: '', location: '', contact_phone: '',
  category: '', related_product: '',
};

const SellItem = () => {
  const { slug } = useParams();
  const isEdit = !!slug;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [images, setImages] = useState([]);           // already saved
  const [pendingFiles, setPendingFiles] = useState([]); // { file, previewUrl, is_primary, id }
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [listing, setListing] = useState(null);

  useEffect(() => {
    Promise.all([
      getCategories({ page_size: 100 }),
      getProducts({ page_size: 200, ordering: 'title' }),
    ]).then(([c, p]) => {
      setCategories(c.data.results || []);
      setProducts(p.data.results || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) {
      // Pre-fill contact details from the profile
      setForm((f) => ({ ...f, contact_phone: user?.phone || '' }));
      return;
    }
    getSecondHandItem(slug)
      .then(({ data }) => {
        if (!data.is_mine) {
          toast.error('You can only edit your own listing');
          navigate('/second-hand');
          return;
        }
        setListing(data);
        setForm({
          title: data.title || '', description: data.description || '',
          price: data.price || '', original_price: data.original_price || '',
          condition: data.condition || 'good', usage_details: data.usage_details || '',
          purchase_year: data.purchase_year || '', accessories: data.accessories || '',
          warranty_remaining: data.warranty_remaining || '', location: data.location || '',
          contact_phone: data.contact_phone || '',
          category: data.category || '', related_product: data.related_product || '',
        });
        setImages(data.images || []);
      })
      .catch(() => {
        toast.error('Listing not found');
        navigate('/dashboard?tab=listings');
      })
      .finally(() => setLoading(false));
  }, [slug, isEdit, navigate, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newPending = files.map((file, i) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      is_primary: images.length === 0 && pendingFiles.length === 0 && i === 0,
      id: Date.now() + i,
    }));
    setPendingFiles((prev) => [...prev, ...newPending]);
    e.target.value = '';
  };

  const removePending = (id) => {
    setPendingFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const setPendingPrimary = (id) => {
    setPendingFiles((prev) => prev.map((p) => ({ ...p, is_primary: p.id === id })));
    setImages((prev) => prev.map((img) => ({ ...img, is_primary: false })));
  };

  const handleDeleteSavedImage = async (imgId) => {
    try {
      await deleteSecondHandImage(imgId);
      setImages((prev) => prev.filter((i) => i.id !== imgId));
      toast.success('Photo removed');
    } catch { toast.error('Could not remove photo'); }
  };

  const uploadPendingImages = async (itemId) => {
    for (let i = 0; i < pendingFiles.length; i++) {
      const pf = pendingFiles[i];
      const fd = new FormData();
      fd.append('item', itemId);
      fd.append('image', pf.file);
      fd.append('alt', form.title);
      fd.append('order', String(images.length + i));
      fd.append('is_primary', pf.is_primary ? 'true' : 'false');
      await uploadSecondHandImage(fd);
    }
  };

  const validate = () => {
    if (!form.title.trim()) { toast.error('Give your listing a title'); return false; }
    if (!form.price || parseFloat(form.price) <= 0) { toast.error('Enter a valid asking price'); return false; }
    if (!isEdit && pendingFiles.length === 0) { toast.error('Add at least one photo'); return false; }
    if (isEdit && images.length === 0 && pendingFiles.length === 0) { toast.error('Add at least one photo'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.price,
        original_price: form.original_price || null,
        purchase_year: form.purchase_year || null,
        category: form.category || null,
        related_product: form.related_product || null,
      };

      let saved;
      if (isEdit) {
        ({ data: saved } = await updateSecondHandItem(slug, payload));
      } else {
        ({ data: saved } = await createSecondHandItem(payload));
      }

      if (pendingFiles.length) await uploadPendingImages(saved.id);

      toast.success(
        isEdit
          ? 'Listing updated — it goes back to the team for review'
          : 'Listing submitted! An admin will review it shortly.'
      );
      navigate(`/second-hand/${saved.slug}`);
    } catch (err) {
      const data = err.response?.data;
      const msg = typeof data === 'string'
        ? data
        : data?.detail || Object.values(data || {})[0] || 'Could not save your listing';
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="sell-page container">
      <div className="skeleton" style={{ height: 500, borderRadius: 8 }} />
    </div>
  );

  return (
    <div className="sell-page">
      <div className="container sell-container">
        <button className="btn btn-ghost back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>

        <div className="sell-header">
          <h1 className="page-title">{isEdit ? 'Edit Listing' : 'Sell Your DJI Gear'}</h1>
          <p className="text-muted">
            List a pre-owned DJI drone, camera or accessory. Our team reviews every listing
            before it appears in the marketplace.
          </p>
        </div>

        {isEdit && listing?.status === 'rejected' && listing?.rejection_reason && (
          <div className="sell-note rejected">
            <FiInfo />
            <div>
              <strong>Why it was rejected</strong>
              <span>{listing.rejection_reason}</span>
            </div>
          </div>
        )}

        <div className="sell-note">
          <FiClock />
          <div>
            <strong>Approval required</strong>
            <span>
              Your listing stays private until an admin approves it. You can always find it
              under <Link to="/dashboard?tab=listings">My Listings</Link>.
            </span>
          </div>
        </div>

        <form className="card sell-form" onSubmit={handleSubmit}>
          <div className="sell-grid">
            <div className="form-group span-2">
              <label className="form-label">Title *</label>
              <input name="title" value={form.title} onChange={handleChange}
                placeholder="e.g. DJI Mavic 3 Pro with Fly More Combo" />
            </div>

            <div className="form-group">
              <label className="form-label">Asking Price (NPR) *</label>
              <input name="price" type="number" min="0" step="0.01" value={form.price}
                onChange={handleChange} placeholder="250000" />
            </div>

            <div className="form-group">
              <label className="form-label">Original Price (NPR)</label>
              <input name="original_price" type="number" min="0" step="0.01"
                value={form.original_price} onChange={handleChange} placeholder="340000" />
            </div>

            <div className="form-group">
              <label className="form-label">Condition *</label>
              <select name="condition" value={form.condition} onChange={handleChange}>
                {Object.entries(CONDITION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select name="category" value={form.category} onChange={handleChange}>
                <option value="">Select a category</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="form-group span-2">
              <label className="form-label">Which DJI product is this?</label>
              <select name="related_product" value={form.related_product} onChange={handleChange}>
                <option value="">Not in our catalogue / other</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
              <small className="field-hint">
                Linking your listing to a catalogue product helps buyers compare it with the new version.
              </small>
            </div>

            <div className="form-group span-2">
              <label className="form-label">Description</label>
              <textarea name="description" rows={5} value={form.description} onChange={handleChange}
                placeholder="Tell buyers about the item, why you are selling it, and any flaws they should know about." />
            </div>

            <div className="form-group">
              <label className="form-label">Usage</label>
              <input name="usage_details" value={form.usage_details} onChange={handleChange}
                placeholder="40 flight hours, 12 battery cycles" />
            </div>

            <div className="form-group">
              <label className="form-label">Year Purchased</label>
              <input name="purchase_year" type="number" min="2000" max="2100"
                value={form.purchase_year} onChange={handleChange} placeholder="2024" />
            </div>

            <div className="form-group">
              <label className="form-label">Warranty Remaining</label>
              <input name="warranty_remaining" value={form.warranty_remaining} onChange={handleChange}
                placeholder="6 months DJI Care" />
            </div>

            <div className="form-group">
              <label className="form-label">Location</label>
              <input name="location" value={form.location} onChange={handleChange}
                placeholder="Kathmandu" />
            </div>

            <div className="form-group span-2">
              <label className="form-label">Accessories Included</label>
              <textarea name="accessories" rows={2} value={form.accessories} onChange={handleChange}
                placeholder="3 batteries, ND filter set, original case, charger" />
            </div>

            <div className="form-group span-2">
              <label className="form-label">Contact Phone</label>
              <input name="contact_phone" value={form.contact_phone} onChange={handleChange}
                placeholder="98XXXXXXXX" />
              <small className="field-hint">Only our team sees this — buyers order through the store.</small>
            </div>
          </div>

          {/* Photos */}
          <div className="sell-photos">
            <label className="form-label">Photos *</label>
            <div className="photo-grid">
              {images.map((img) => (
                <div key={img.id} className={`photo-tile ${img.is_primary ? 'primary' : ''}`}>
                  <img src={img.image} alt={img.alt || ''} />
                  {img.is_primary && <span className="photo-badge"><FiStar /> Cover</span>}
                  <button type="button" className="photo-remove"
                    onClick={() => handleDeleteSavedImage(img.id)}><FiX /></button>
                </div>
              ))}
              {pendingFiles.map((pf) => (
                <div key={pf.id} className={`photo-tile ${pf.is_primary ? 'primary' : ''}`}>
                  <img src={pf.previewUrl} alt="" />
                  {pf.is_primary
                    ? <span className="photo-badge"><FiStar /> Cover</span>
                    : <button type="button" className="photo-set-primary"
                        onClick={() => setPendingPrimary(pf.id)}>Make cover</button>}
                  <button type="button" className="photo-remove"
                    onClick={() => removePending(pf.id)}><FiX /></button>
                </div>
              ))}
              <label className="photo-upload">
                <FiUpload />
                <span>Add photos</span>
                <input type="file" accept="image/*" multiple onChange={handleFileSelect} hidden />
              </label>
            </div>
          </div>

          <div className="sell-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Submitting...' : isEdit ? 'Save & Resubmit' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellItem;
